import { useState } from "react";

import {
  faClock,
  faEye,
  faPencil,
  faPlus,
  faRocket,
  faShieldAlt,
  faTrash,
  faUserCheck,
  faUserPlus,
  IconDefinition,
} from "@fortawesome/pro-regular-svg-icons";
import { find } from "lodash-es";
import { Navigate, useSearchParams } from "react-router-dom";

import { AddUserToRoleModal } from "src/adminPanel/AddUserToRoleModal";
import {
  useAllOrgs,
  useDeleteRole,
  useUsersByRole,
} from "src/adminPanel/queries";
import { Button } from "src/base-components/Button";
import { Select } from "src/base-components/Select";
import { RoleInDB, User } from "src/clients/taktile-api";
import { Avatar } from "src/design-system/Avatar";
import { Icon } from "src/design-system/Icon";

type Role = {
  name: string;
  displayName: string;
  icon: IconDefinition;
  color: string;
};

const ROLES: Role[] = [
  {
    name: "oncall_group",
    displayName: "On-Call Group Users (includes support heroes)",
    icon: faEye,
    color: "bg-orange-50 border-orange-200",
  },
  {
    name: "oncall_write",
    displayName: "On-Call Write Users",
    icon: faPencil,
    color: "bg-red-50 border-red-200",
  },
  {
    name: "taktile_d_admin",
    displayName: "Taktile Admin Users",
    icon: faShieldAlt,
    color: "bg-indigo-50 border-indigo-200",
  },
  {
    name: "fwd_d",
    displayName: "Forward Deployed",
    icon: faRocket,
    color: "bg-neo-green-50 border-neo-green-200",
  },
  {
    name: "access_requester",
    displayName: "Access Requesters",
    icon: faUserPlus,
    color: "bg-sky-50 border-sky-200",
  },
  {
    name: "access_approver",
    displayName: "Access Approvers",
    icon: faUserCheck,
    color: "bg-green-50 border-green-200",
  },
];

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  );

  if (diffInDays === 0) {
    return "Today";
  } else if (diffInDays === 1) {
    return "Yesterday";
  } else if (diffInDays < 7) {
    return `${diffInDays} days ago`;
  } else {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
    });
  }
};

const RoleSection: React.FC<{ role: Role }> = ({ role }) => {
  const allOrgs = useAllOrgs();
  const queryResult = useUsersByRole(role.name);
  const deleteRole = useDeleteRole();
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false);

  const handleDeleteUserRole =
    (roleId: string, userEmail: string) => async () => {
      if (
        window.confirm(
          `Are you sure you want to remove the ${role.displayName} role from ${userEmail}?`,
        )
      ) {
        await deleteRole.mutateAsync(roleId);
      }
    };

  const showOrganization = role.name === "fwd_d";

  // Flatten all pages of users into a single array
  const allUsers = queryResult.data?.pages.flatMap((page) => page.users) ?? [];

  const renderContent = () => {
    if (queryResult.isLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500">Loading users...</div>
        </div>
      );
    }

    if (queryResult.isError) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="text-red-500">Error loading users</div>
        </div>
      );
    }

    return (
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        {allUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
              <Icon icon={role.icon} size="md" />
            </div>
            <h3 className="mb-2 font-medium text-gray-900 text-lg">
              No users found
            </h3>
            <p className="mb-4 text-gray-500 text-sm">
              No users have been assigned the {role.displayName.toLowerCase()}{" "}
              role yet.
            </p>
            <Button
              iconLeft={faPlus}
              size="sm"
              variant="secondary"
              onClick={() => setIsAddUserModalOpen(true)}
            >
              Add First User
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left font-inter-normal-12px font-semibold text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  {showOrganization && (
                    <th className="px-4 py-3 text-left font-inter-normal-12px font-semibold text-gray-500 uppercase tracking-wider">
                      Organization
                    </th>
                  )}
                  <th className="px-4 py-3 text-left font-inter-normal-12px font-semibold text-gray-500 uppercase tracking-wider">
                    Role Assigned
                  </th>
                  <th className="px-4 py-3 text-left font-inter-normal-12px font-semibold text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {allUsers.map((user: User) => (
                  <tr
                    key={user.id}
                    className="transition-colors hover:bg-gray-50"
                  >
                    <td className="whitespace-nowrap px-4 py-3">
                      <div className="flex items-center">
                        <Avatar size="lg" user={user} />
                        <div className="ml-3">
                          <div className="font-medium text-gray-900 text-sm">
                            {user.full_name ||
                              user.email ||
                              user.username ||
                              "Unknown User"}
                          </div>
                          <div className="font-inter-normal-12px text-gray-500">
                            {user.email && user.email !== user.username
                              ? user.email
                              : `ID: ${user.id}`}
                          </div>
                        </div>
                      </div>
                    </td>
                    {showOrganization && (
                      <td className="whitespace-nowrap px-4 py-3 text-gray-900 text-sm">
                        {user.roles
                          ?.filter(
                            (userRole: RoleInDB) => userRole.name === role.name,
                          )
                          .map((userRole: RoleInDB) => (
                            <div
                              key={userRole.id}
                              className="flex items-center gap-2"
                            >
                              <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 font-inter-medium-12px text-blue-800">
                                {find(allOrgs.data, {
                                  id: userRole.arguments?.org_id,
                                })?.name || "Unknown Organization"}
                              </span>
                            </div>
                          ))}
                      </td>
                    )}
                    <td className="whitespace-nowrap px-4 py-3 text-gray-900 text-sm">
                      {user.roles
                        ?.filter(
                          (userRole: RoleInDB) => userRole.name === role.name,
                        )
                        .map((userRole: RoleInDB) => (
                          <div
                            key={userRole.id}
                            className="flex items-center gap-2"
                          >
                            <div className="flex items-center gap-1 font-inter-normal-12px text-gray-500">
                              <Icon icon={faClock} size="xs" />
                              <span>{formatDate(userRole.created_at)}</span>
                            </div>
                          </div>
                        ))}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-gray-500 text-sm">
                      {user.roles
                        ?.filter(
                          (userRole: RoleInDB) => userRole.name === role.name,
                        )
                        .map((userRole: RoleInDB) => (
                          <Button
                            key={userRole.id}
                            iconLeft={faTrash}
                            size="sm"
                            variant="warning"
                            onClick={handleDeleteUserRole(
                              userRole.id,
                              user.email || user.username || "this user",
                            )}
                          >
                            Remove
                          </Button>
                        ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {queryResult.hasNextPage && (
              <div className="flex justify-center border-gray-200 border-t px-4 py-3">
                <Button
                  disabled={queryResult.isFetchingNextPage}
                  size="sm"
                  variant="secondary"
                  onClick={() => queryResult.fetchNextPage()}
                >
                  {queryResult.isFetchingNextPage ? "Loading..." : "Load More"}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mb-8">
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-xl border-2 ${role.color}`}
          >
            <Icon icon={role.icon} size="md" />
          </div>
          <div>
            <h4 className="font-bold text-2xl text-gray-900">
              {role.displayName}
            </h4>
            <p className="text-gray-500 text-sm">
              Manage users with {role.displayName.toLowerCase()} permissions
            </p>
          </div>
        </div>
        <Button
          iconLeft={faPlus}
          size="sm"
          variant="primary"
          onClick={() => setIsAddUserModalOpen(true)}
        >
          Add User
        </Button>
      </div>
      {renderContent()}
      <AddUserToRoleModal
        isOpen={isAddUserModalOpen}
        roleName={role.name}
        roleTitle={role.displayName}
        onClose={() => setIsAddUserModalOpen(false)}
      />
    </div>
  );
};

export const RoleBasedUsers: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedRole = ROLES.find(
    (r) => r.name === (searchParams.get("role") ?? ""),
  );

  const roleOptions = ROLES.map((role) => ({
    key: role.name,
    value: role.displayName,
  }));

  if (!selectedRole) {
    const defaultRole = ROLES[0];
    if (!defaultRole) return null;
    return <Navigate replace to={`?role=${defaultRole.name}`} />;
  }

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <h1 className="font-bold text-3xl text-gray-900">Role Management</h1>
          <p className="mt-2 text-gray-600 text-lg">
            Manage user roles and permissions across the platform
          </p>
        </div>

        <div className="w-full">
          <Select
            dataLoc="role-select"
            options={roleOptions}
            value={selectedRole.name}
            fullWidth
            onChange={(roleName) => {
              setSearchParams(
                (prev) => {
                  prev.set("role", roleName);
                  return prev;
                },
                { replace: true },
              );
            }}
            buttonRenderer={(option) => (
              <div className="flex items-center rounded-lg bg-white py-1 text-base text-gray-900">
                {option?.value ?? "Select a role"}
              </div>
            )}
          />

          <div className="mx-auto mt-4">
            <RoleSection key={selectedRole.name} role={selectedRole} />
          </div>
        </div>
      </div>
    </div>
  );
};
